import { z } from "zod";

// 1. Per-component props schemas (strict to reject unknown props)
const CardPropsSchema = z
  .object({
    title: z.string().nullable(),
    padding: z.enum(["sm", "md", "lg"]).nullable(),
  })
  .strict();

const TextPropsSchema = z
  .object({
    content: z.string().nullable(), // Static text
    valuePath: z.string().nullable(), // Dynamic via JSON Pointer
  })
  .strict()
  .refine((data) => Boolean(data.content) !== Boolean(data.valuePath), {
    message: "Text must have either content OR valuePath, not both or neither",
  });

const DataTablePropsSchema = z
  .object({
    dataPath: z.string(), // e.g., "/invoices"
    columns: z.array(
      z.object({
        key: z.string(), // Field key, e.g., "totalAmount"
        label: z.string(), // Display label, e.g., "Amount"
        format: z.enum(["currency", "date", "status", "text"]).optional(),
      }),
    ),
  })
  .strict();

const HeadingPropsSchema = z
  .object({
    level: z.enum(["1", "2", "3", "4"]),
    content: z.string(),
  })
  .strict();

const DividerPropsSchema = z.object({}).strict();

// 2. Discriminated union for element validation
export const PortalUIElementSchema = z.discriminatedUnion("type", [
  z
    .object({
      key: z.string(),
      type: z.literal("Card"),
      props: CardPropsSchema,
      children: z.array(z.string()).optional(),
    })
    .strict(),
  z
    .object({
      key: z.string(),
      type: z.literal("Text"),
      props: TextPropsSchema,
      children: z.array(z.string()).optional(),
    })
    .strict(),
  z
    .object({
      key: z.string(),
      type: z.literal("DataTable"),
      props: DataTablePropsSchema,
      children: z.array(z.string()).optional(),
    })
    .strict(),
  z
    .object({
      key: z.string(),
      type: z.literal("Heading"),
      props: HeadingPropsSchema,
      children: z.array(z.string()).optional(),
    })
    .strict(),
  z
    .object({
      key: z.string(),
      type: z.literal("Divider"),
      props: DividerPropsSchema,
      children: z.array(z.string()).optional(),
    })
    .strict(),
]);

export const PortalUITreeSchema = z.object({
  root: z.string(),
  elements: z.record(z.string(), PortalUIElementSchema),
});

// 4. Inferred types
export type PortalUIElement = z.infer<typeof PortalUIElementSchema>;
export type PortalUITree = z.infer<typeof PortalUITreeSchema>;

// 5. Constants
export const ALLOWED_DATA_PATHS = [
  "/customer",
  "/portalUser",
  "/invoices",
  "/orders",
] as const;

// 6. Validation functions
export function validateDataPath(path: string): boolean {
  return ALLOWED_DATA_PATHS.some((allowed) => path.startsWith(allowed));
}

export function validatePortalUITree(tree: unknown): {
  success: boolean;
  error?: string;
} {
  // Schema validation
  const parseResult = PortalUITreeSchema.safeParse(tree);
  if (!parseResult.success) {
    return { success: false, error: parseResult.error.message };
  }

  const validTree = parseResult.data;

  // Structural validation
  // Rule 1: Root exists
  if (!validTree.elements[validTree.root]) {
    return {
      success: false,
      error: `Root key '${validTree.root}' not found in elements`,
    };
  }

  for (const mapKey in validTree.elements) {
    const element = validTree.elements[mapKey];
    if (!element) continue;

    if (element.key !== mapKey) {
      return {
        success: false,
        error: `Element '${mapKey}' has mismatched key (expected '${mapKey}', got '${element.key}')`,
      };
    }

    if (element.children) {
      for (const childKey of element.children) {
        if (!validTree.elements[childKey]) {
          return {
            success: false,
            error: `Element '${element.key}' references non-existent child '${childKey}'`,
          };
        }
      }
    }
  }

  return { success: true };
}

// 7. Catalog prompt generator
export function generateCatalogPrompt(): string {
  return `
Available components for JSON portal:

1. Card - Container with optional title and padding
   Props: { title?: string, padding?: "sm" | "md" | "lg" }
   Can have children.

2. Text - Display static or dynamic text
   Props: { content: string } OR { valuePath: string }
   - content: Static text
   - valuePath: JSON Pointer to data (e.g., "/customer/name")
   Use ONE of content or valuePath, not both.

3. DataTable - Display tabular data
   Props: { dataPath: string, columns: [{ key, label, format? }] }
   - dataPath: JSON Pointer to array (e.g., "/invoices")
   - columns[].key: Field name in each item
   - columns[].label: Column header
   - columns[].format: "currency" | "date" | "status" | "text"

4. Heading - Section header
   Props: { level: "1"|"2"|"3"|"4", content: string }

5. Divider - Horizontal separator
   Props: {} (none)

Available data paths:
- /customer/name
- /portalUser/name
- /invoices (array)
- /orders (array)

Do NOT include parentKey or visible fields.
`.trim();
}
